// Month-End Close (MEC) — API client wrappers
//
// All requests go through the shared JWT-authenticated `api.get/put/post`
// helpers from ./client.js — same base URL, same refresh logic, same error
// shape.
//
// Endpoints (per MEC_REQUIREMENTS.md §5 and api/mec/routes.py):
//   GET  /api/mec/routing                                        → list per-shop routing rows
//   GET  /api/mec/qbo/{realm_id}/accounts                        → live Chart of Accounts
//   GET  /api/mec/qbo/{realm_id}/classes                         → live Class list
//   PUT  /api/mec/routing/{shop_id}                              → update one routing row
//   POST /api/mec/close/run                                      → execute a close run (draft or finalize)
//   GET  /api/mec/close/{shop_id}/{period_start}/{period_end}    → read cached close-run snapshot
//   GET  /api/mec/inventory-excluded?shop_id&period_start&period_end_exclusive
//        → D-005 inventory-sourced parts exclusion breakdown (auditable card)
//
// IMPORTANT: never call QBO directly from the browser. The backend is the
// only system that talks to Intuit. Everything in this file targets our own
// /api/mec/* surface.

import { api, tokenStorage } from './client';

const BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * GET /api/mec/routing
 * @returns {Promise<{shops: Array<{shop_id, shop_name, realm_id, parts_purchases_account_id, parts_purchases_account_name, sublet_account_id, sublet_account_name, qbo_class_id, qbo_class_name, updated_at}>}>}
 */
export function listRouting() {
  return api.get('/api/mec/routing');
}

/**
 * GET /api/mec/qbo/{realm_id}/accounts?account_type=...
 * Fresh from QBO every call — no client-side caching.
 */
export function getAccountsForRealm(realmId, accountType = 'All') {
  const qs = new URLSearchParams({ account_type: accountType }).toString();
  return api.get(`/api/mec/qbo/${encodeURIComponent(realmId)}/accounts?${qs}`);
}

/**
 * GET /api/mec/qbo/{realm_id}/classes
 * Fresh from QBO every call — no client-side caching.
 */
export function getClassesForRealm(realmId) {
  return api.get(`/api/mec/qbo/${encodeURIComponent(realmId)}/classes`);
}

/**
 * PUT /api/mec/routing/{shop_id}
 * Persists the per-shop account/class selection.
 */
export function updateRouting(shopId, data) {
  return api.put(`/api/mec/routing/${encodeURIComponent(shopId)}`, data);
}

/**
 * POST /api/mec/close/run
 * Execute a close run for one (shop, period). `finalize: false` runs in
 * draft mode — numbers are persisted but the period is not locked.
 *
 * Returns the full close payload with:
 *   close_run_id, shop_id, period_start, period_end_inclusive, status,
 *   parts: { a_used_cents, a_pending_return_cents, a_total_cents,
 *            b_received_cents, b_pending_return_cents, b_total_cents,
 *            b0_cents, delta_wip_cents, p_cents, variance_cents,
 *            match_tally: { tier_1, tier_2, tier_3_no_po, ambiguous, ... } },
 *   sublet: { same shape, no a_pending_return / b_pending_return }
 */
export function runClose(shopId, periodStart, periodEndInclusive, finalize = false) {
  return api.post('/api/mec/close/run', {
    shop_id: shopId,
    period_start: periodStart,
    period_end_inclusive: periodEndInclusive,
    finalize,
  });
}

/**
 * GET /api/mec/close/{shop_id}/{period_start}/{period_end_inclusive}
 * Read the persisted close-run snapshot (404 if no run yet).
 *
 * Returns a flattened parts-only payload; sublet detail lives on the
 * mec_close_line snapshot which this endpoint doesn't currently surface
 * (POST /close/run does). For Dashboard v1 we treat GET as the cheap
 * cache-check; if it 404s, we POST /close/run to populate.
 */
export function getClose(shopId, periodStart, periodEndInclusive) {
  return api.get(
    `/api/mec/close/${encodeURIComponent(shopId)}/${encodeURIComponent(periodStart)}/${encodeURIComponent(periodEndInclusive)}`
  );
}

/**
 * GET /api/mec/inventory-excluded?shop_id=&period_start=&period_end_exclusive=
 * D-005 auditable card data — the inventory-sourced parts excluded from
 * Query A. Returns `{shop_id, grand_total_cents, breakdown: [{brand, name,
 * line_count, total_cents}]}`.
 *
 * The Backend agent is wiring this in parallel. If the endpoint isn't
 * deployed yet, the caller should handle the 404 gracefully (show an empty
 * "exclusion details not available yet" state on the card).
 */
export function getInventoryExcluded(shopId, periodStart, periodEndExclusive) {
  const qs = new URLSearchParams({
    shop_id: shopId,
    period_start: periodStart,
    period_end_exclusive: periodEndExclusive,
  }).toString();
  return api.get(`/api/mec/inventory-excluded?${qs}`);
}

/**
 * GET /api/mec/closes/recent?limit=N
 * Returns the N most-recent close runs visible to the current user
 * (filtered to their shop_ids when they aren't all-shops).
 *
 * Response shape (array of objects):
 *   close_run_id, shop_id, shop_name, period_start, period_end,
 *   status, p_cents, variance_cents, created_at, finalized_at
 */
export function listRecentCloses(limit = 10) {
  const qs = new URLSearchParams({ limit: String(limit) }).toString();
  return api.get(`/api/mec/closes/recent?${qs}`);
}

// ───────────────────────────────────────────────────────────────
// Tier 2 review queue + per-close match list (§6 mec_match audit)
// ───────────────────────────────────────────────────────────────

/**
 * GET /api/mec/match/{close_run_id}?domain=parts|sublet
 * List mec_match rows for a finalized close run. Used by the location
 * drill-down and (filtered to pending_review) the Tier 2 queue.
 */
export function listMatches(closeRunId, domain = 'parts') {
  const qs = new URLSearchParams({ domain }).toString();
  return api.get(
    `/api/mec/match/${encodeURIComponent(closeRunId)}?${qs}`
  );
}

/**
 * POST /api/mec/match/{match_id}/confirm — Mode A approve
 * Backend takes no body. `notes` parameter retained for forward-compat but
 * not transmitted (backend doesn't accept a body on this endpoint).
 */
export function confirmMatch(matchId /*, notes */) {
  return api.post(
    `/api/mec/match/${encodeURIComponent(matchId)}/confirm`,
    null,
  );
}

/**
 * POST /api/mec/match/{match_id}/reject?reason=... — Mode A reject
 * Backend takes `reason` as a Query parameter (not a JSON body). Without
 * this, the reject reason gets silently dropped on the server side.
 */
export function rejectMatch(matchId, reason) {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  return api.post(
    `/api/mec/match/${encodeURIComponent(matchId)}/reject${qs}`,
    null,
  );
}

// ───────────────────────────────────────────────────────────────
// Journal Entry generator (View 4 — §7)
// ───────────────────────────────────────────────────────────────

/**
 * GET /api/mec/journal-entry/{close_run_id}?format=consolidated|class-split
 * Returns the JE text/CSV/lines payload. Backend agent may still be
 * deploying — callers should treat 404 as "endpoint not yet there".
 */
export function getJournalEntry(closeRunId, format = 'consolidated') {
  const qs = new URLSearchParams({ format }).toString();
  return api.get(
    `/api/mec/journal-entry/${encodeURIComponent(closeRunId)}?${qs}`
  );
}

/**
 * GET /api/mec/journal-entry/{close_run_id}/export?format=csv|text
 * Triggers a file download in the user's browser. Returns the blob URL
 * so the caller can revoke it after the download is triggered.
 */
export async function downloadJournalEntry(closeRunId, format = 'csv') {
  const url = `${BASE_URL}/api/mec/journal-entry/${encodeURIComponent(closeRunId)}/export?format=${encodeURIComponent(format)}`;
  const headers = {};
  const token = tokenStorage.getAccess();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ───────────────────────────────────────────────────────────────
// Match Mode (A / B / C) per shop (§6 Mode toggle)
// ───────────────────────────────────────────────────────────────

/**
 * GET /api/mec/match-mode → list per-shop Mode A/B/C
 * Returns `{shops: [{shop_id, shop_name, mode, threshold, updated_at}]}`.
 */
export function listMatchModes() {
  return api.get('/api/mec/match-mode');
}

/**
 * PUT /api/mec/match-mode/{shop_id}
 * mode ∈ {'A', 'B', 'C'}. threshold only meaningful for Mode C (0.0-1.0).
 */
export function updateMatchMode(shopId, mode, threshold) {
  const body = { mode };
  if (mode === 'C' && threshold != null) body.threshold = threshold;
  return api.put(
    `/api/mec/match-mode/${encodeURIComponent(shopId)}`,
    body
  );
}

// ───────────────────────────────────────────────────────────────
// Two-tier PO audit (D-04x — Ambiguous + Tier 1 total-mismatch redesign)
// ───────────────────────────────────────────────────────────────

/**
 * GET /api/mec/audit/po-summary/{close_run_id}?method=...&domain=...
 * Top-tier table — one row per PO with totals, counts, and net delta.
 *
 * `method` ∈ {'ambiguous', 'tier_1_total_mismatch'}.
 * `domain` ∈ {'parts', 'sublet'}.
 */
export function listPoAuditSummary(closeRunId, method, domain = 'parts') {
  const qs = new URLSearchParams({ method, domain }).toString();
  return api.get(
    `/api/mec/audit/po-summary/${encodeURIComponent(closeRunId)}?${qs}`
  );
}

/**
 * GET /api/mec/audit/po/{close_run_id}/{po_number}?domain=...
 * Per-PO vendor reconciliation (the accordion body). Includes status_summary
 * plus a vendors[] array sorted by abs(delta_cents) DESC.
 */
export function getPoAuditDetail(closeRunId, poNumber, domain = 'parts') {
  const qs = new URLSearchParams({ domain }).toString();
  return api.get(
    `/api/mec/audit/po/${encodeURIComponent(closeRunId)}/${encodeURIComponent(poNumber)}?${qs}`
  );
}

export default {
  listRouting,
  getAccountsForRealm,
  getClassesForRealm,
  updateRouting,
  runClose,
  getClose,
  getInventoryExcluded,
  listRecentCloses,
  listMatches,
  confirmMatch,
  rejectMatch,
  getJournalEntry,
  downloadJournalEntry,
  listMatchModes,
  updateMatchMode,
  listPoAuditSummary,
  getPoAuditDetail,
};
